import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Phone, Clock, Car, Accessibility, HeartHandshake, Play } from "lucide-react";
import { CHURCH, IMAGES, NEXT_STEPS } from "../lib/data";
import { Btn, Eyebrow } from "../components/ui-kit";
import { MaskLine, Reveal } from "../lib/motion";
import { Marquee } from "../components/Marquee";
import { useContent, resolveImg } from "../lib/api";

const fmtDate = (d) => {
  if (!d) return "";
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};

const MANIFESTO = [
  { n: "01", t: "Faith That Grows", d: "We create space for real, honest faith — the kind that grows deeper through worship, teaching, and community." },
  { n: "02", t: "Family That Welcomes", d: "No matter where you've been, there's a seat for you here. One church, one family, one love." },
  { n: "03", t: "Love That Serves", d: "We take the love of Christ beyond our walls, serving Little Rock with compassion and purpose." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const { data: SERVICES } = useContent("services");
  const { data: EVENTS } = useContent("events");
  const nextEvent = (EVENTS && EVENTS[0]) || null;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#0A0A0A]">
        <motion.img style={{ y, scale }} src={IMAGES.hero} alt="Worship at One Love Ministries"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-[#0A0A0A]/60" />
        <div className="absolute inset-0 grain" />

        <div className="relative h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-20 sm:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Eyebrow dark>Little Rock, Arkansas</Eyebrow>
          </motion.div>
          <h1 className="font-display font-extrabold tracking-tight text-white text-[13vw] sm:text-7xl lg:text-[6.5rem] leading-[0.92] max-w-5xl">
            <MaskLine delay={0.15}>WELCOME TO</MaskLine>
            <MaskLine delay={0.3} className="text-[#E63946]">ONE LOVE</MaskLine>
            <MaskLine delay={0.45}>MINISTRIES</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-6 text-white/80 text-lg sm:text-xl max-w-xl leading-relaxed">
            <span className="font-display font-bold text-white">One Church. One Family. One Love.</span> A place where faith grows,
            lives are transformed, families are strengthened, and everyone is welcomed with love.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="mt-9 flex flex-wrap gap-4">
            <Btn to="/plan-your-visit" testid="hero-plan-visit">Plan Your Visit <ArrowRight size={16} /></Btn>
            <Btn to="/sermons" variant="ghostLight" testid="hero-watch-online"><Play size={16} /> Watch Online</Btn>
          </motion.div>
        </div>
      </section>

      {/* WELCOME HOME */}
      <section className="py-24 sm:py-32 bg-white" data-testid="welcome-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <Reveal>
            <Eyebrow>Welcome Home</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              A place where you truly <span className="text-[#E63946]">belong.</span>
            </h2>
            <p className="mt-7 text-[#0A0A0A]/70 text-lg leading-relaxed">
              One Love Ministries is a place where people from all backgrounds come together to worship God, grow spiritually,
              build meaningful relationships, and experience genuine love and community.
            </p>
            <p className="mt-4 text-[#0A0A0A]/70 text-lg leading-relaxed">
              You don't have to have everything figured out before you come. Whether you're an individual, a couple, a family
              with children, a young adult, a senior, returning to church, or stepping inside for the very first time —
              there's a place for you here.
            </p>
            <div className="mt-9"><Btn to="/about" variant="outline" testid="welcome-learn-more">Learn More About Us <ArrowRight size={16} /></Btn></div>
          </Reveal>
          <Reveal delay={0.15} className="grid grid-cols-2 gap-4">
            <div className="overflow-hidden rounded-sm aspect-[3/4] mt-8">
              <img src={IMAGES.welcome} alt="Church family" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-sm aspect-[3/4]">
              <img src={IMAGES.fellowship} alt="Fellowship" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO — numbered chapters */}
      <section className="bg-[#0A0A0A] text-white grain relative" data-testid="manifesto-section">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <Eyebrow dark>Our Heart</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
              Love God.<br />Love People.<br /><span className="text-[#E63946]">Serve Our City.</span>
            </h2>
          </div>
          <div className="space-y-2">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className="border-t border-white/10 py-10 flex gap-8">
                <span className="font-display font-extrabold text-[#E63946] text-2xl sm:text-3xl shrink-0">{m.n}</span>
                <div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl mb-3">{m.t}</h3>
                  <p className="text-white/60 text-lg leading-relaxed">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* VISIT US */}
      <section className="py-24 sm:py-32 bg-white" data-testid="visit-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Join Us</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              Join Us at One Love Ministries
            </h2>
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-16">
            <Reveal className="space-y-8">
              <div>
                <h3 className="font-display font-bold text-2xl mb-4 flex items-center gap-2"><Clock size={22} className="text-[#E63946]" /> Service Times</h3>
                <div className="space-y-3">
                  {(SERVICES || []).map((s) => (
                    <div key={s.id || s.name} className="flex items-baseline justify-between border-b border-black/10 pb-3">
                      <div><p className="font-medium">{s.name}</p><p className="text-sm text-black/50">{s.note}</p></div>
                      <span className="text-[#E63946] font-medium whitespace-nowrap">{s.time}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-3 text-lg">
                <MapPin className="text-[#E63946] mt-1 shrink-0" size={22} />
                <div>
                  <p className="font-display font-bold">{CHURCH.name}</p>
                  <p className="text-black/60">{CHURCH.address}, {CHURCH.cityStateZip}</p>
                  <a href={CHURCH.phoneHref} className="text-black/60 hover:text-[#E63946] transition-colors">{CHURCH.phone}</a>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[[Car, "Free on-site parking with easy access"], [Accessibility, "Wheelchair accessible facilities"], [HeartHandshake, "Greeters ready to welcome you"]].map(([Icon, t], i) => (
                  <div key={t} className="p-5 bg-[#F4F4F5] rounded-sm">
                    <Icon className="text-[#E63946] mb-3" size={22} />
                    <p className="text-sm text-black/70 leading-relaxed">{t}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Btn href={CHURCH.directions} testid="visit-directions"><MapPin size={16} /> Get Directions</Btn>
                <Btn href={CHURCH.phoneHref} variant="outline" testid="visit-call"><Phone size={16} /> Call Us</Btn>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="overflow-hidden rounded-sm min-h-[420px] border border-black/10">
              <iframe title="One Love Ministries location" src={CHURCH.mapEmbed} className="w-full h-full min-h-[420px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* NEXT EVENT + COMMUNITY BENTO */}
      <section className="py-24 sm:py-32 bg-[#F4F4F5]" data-testid="community-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <Eyebrow>Next Up</Eyebrow>
              <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">Something's happening.</h2>
            </div>
            <Btn to="/events" variant="outline" testid="home-view-events">View All Events <ArrowRight size={16} /></Btn>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-4">
            {nextEvent && (
            <Reveal className="lg:col-span-12 group relative overflow-hidden rounded-sm min-h-[380px] bg-[#0A0A0A]">
              <img src={resolveImg(nextEvent.image)} alt={nextEvent.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
              <div className="relative p-8 sm:p-10 h-full flex flex-col justify-end text-white">
                <span className="inline-block w-fit text-xs uppercase tracking-[0.2em] bg-[#E63946] px-3 py-1 rounded-full mb-4">{nextEvent.category}</span>
                <h3 className="font-display font-bold text-3xl sm:text-4xl mb-2">{nextEvent.title}</h3>
                <p className="text-white/70 mb-4 max-w-lg">{nextEvent.desc}</p>
                <p className="text-white/90 flex items-center gap-2 text-sm"><Clock size={15} />{fmtDate(nextEvent.date)} · {nextEvent.time}</p>
              </div>
            </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="py-24 sm:py-32 bg-white" data-testid="next-steps-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Eyebrow>Get Involved</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">Take Your Next Step</h2>
            <p className="mt-5 text-black/60 text-lg">Wherever you are on your journey, there's a next step for you at One Love.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {NEXT_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <Link to={`/contact?subject=${encodeURIComponent(s.subject)}`} data-testid={`next-step-${i}`}
                  className="group block p-8 border border-black/10 rounded-sm hover:border-[#E63946] hover:bg-[#0A0A0A] transition-colors duration-300 h-full">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-display font-extrabold text-2xl group-hover:text-white transition-colors">0{i + 1}</span>
                    <ArrowUpRight className="text-black/30 group-hover:text-[#E63946] transition-colors" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2 group-hover:text-white transition-colors">{s.title}</h3>
                  <p className="text-black/60 group-hover:text-white/60 transition-colors">{s.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden bg-[#E63946] text-white">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-6">We saved you a seat</p>
            <h2 className="font-display font-extrabold tracking-tight text-5xl sm:text-7xl leading-[0.95] mb-8">
              Come as you are.
            </h2>
            <div className="flex flex-wrap gap-4 justify-center">
              <Btn to="/plan-your-visit" variant="dark" testid="cta-plan-visit">Plan Your Visit</Btn>
              <Btn to="/give" variant="ghostLight" testid="cta-give">Give</Btn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
